import React, { ReactNode, useCallback, useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Section, SectionList } from '../../../components/common/SectionList'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { ResidentListItem } from '../../../components/society/ResidentList/ResidentListItem'
import { IBoardRoomEventModel } from '../../../interfaces/models/boardroom-events.interfaces'
import {
  IAttendance,
  IEventModel,
} from '../../../interfaces/models/events.interfaces'
import { societyQueries } from '../../../api/societies'
import { unitQueries } from '../../../api/units'
import { userQueries } from '../../../api/users'
import { IUserModel } from '../../../interfaces/models/users.interfaces'
import { getUserFullName } from '../../../helpers/user'

interface EventDetailAttendantsProps {
  event: IEventModel | IBoardRoomEventModel
  boardRoomEvent?: boolean
}

const getUserAttendance = (
  _user: IUserModel,
  attendanceList: IAttendance[]
): { user: IUserModel; status: string } => {
  const foundAttendance = attendanceList.find(
    (attendance) => attendance.userId === _user._id
  )
  if (!foundAttendance) return { user: _user, status: 'no_reply' }
  return { user: _user, status: foundAttendance.status }
}

export const EventDetailAttendants = ({
  event,
  boardRoomEvent,
}: EventDetailAttendantsProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { data: society } = useQuery(societyQueries.getSociety(event.societyId))

  const { data: units } = useQuery(unitQueries.getUnitsPerSociety(society?._id))
  const residentsList = units?.flatMap((unit) => unit.residentsList || [])
  const residentsQuery = useQueries({
    queries:
      residentsList?.map((resident) => userQueries.getUser(resident.userId)) ||
      [],
  })
  const residentUsers = residentsQuery.flatMap((query) => query.data || [])

  const boardMembersList = society?.boardMembersList || []
  const boardMembersQuery = useQueries({
    queries:
      boardMembersList?.map((board) => userQueries.getUser(board.userId)) || [],
  })

  const boardUsers = boardMembersQuery.flatMap((query) => query.data || [])

  const replyUsers = useMemo((): {
    user: IUserModel
    status: string
  }[] => {
    const userRepliesMap = new Map<
      string,
      { user: IUserModel; status: string }
    >()

    const getUsers = (): IUserModel[] => {
      let users: IUserModel[] = []
      if (boardRoomEvent) {
        users = boardUsers
      } else {
        users = residentUsers
      }

      return users
    }
    const allUsers = getUsers()
    allUsers.forEach((user) => {
      if (userRepliesMap.has(user._id)) return
      userRepliesMap.set(
        user._id,
        getUserAttendance(user, event.attendance || [])
      )
    })

    const userReplies: {
      user: IUserModel
      status: string
    }[] = []
    userRepliesMap.forEach((userReply) => {
      userReplies.push(userReply)
    })
    userReplies.sort((a, b) =>
      getUserFullName(a.user).localeCompare(getUserFullName(b.user))
    )

    return userReplies
  }, [boardRoomEvent, boardUsers, event.attendance, residentUsers])

  const attending = replyUsers.filter((user) => user.status === 'attending')
  const notAttending = replyUsers.filter(
    (user) => user.status === 'not_attending'
  )
  const noReplyUsers = replyUsers.filter((user) => user.status === 'no_reply')

  const getNonEmptySections = useCallback((): Section[] => {
    if (!society) return []
    const _sections: (Section | null)[] = [
      attending.length > 0
        ? {
            title: `${translate(
              'eventDetailView.attendance.titles.attending'
            )} (${attending.length})`,
            item: (
              <div>
                {attending.map((attendance) => (
                  <ResidentListItem
                    key={attendance.user._id}
                    user={attendance.user}
                    society={society}
                    showUserRoles={false}
                    surnameFirst={false}
                  />
                ))}
              </div>
            ),
          }
        : null,
      notAttending.length > 0
        ? {
            title: `${translate(
              'eventDetailView.attendance.titles.notAttending'
            )} (${notAttending.length})`,
            item: (
              <div>
                {notAttending.map((attendance) => (
                  <ResidentListItem
                    key={attendance.user._id}
                    user={attendance.user}
                    society={society}
                    showUserRoles={false}
                    surnameFirst={false}
                  />
                ))}
              </div>
            ),
          }
        : null,
      noReplyUsers.length > 0
        ? {
            title: `${translate(
              'eventDetailView.attendance.titles.noReply'
            )} (${noReplyUsers.length})`,
            item: (
              <div>
                {noReplyUsers.map((attendance) => (
                  <ResidentListItem
                    key={attendance.user._id}
                    user={attendance.user}
                    society={society}
                    showUserRoles={false}
                    surnameFirst={false}
                  />
                ))}
              </div>
            ),
          }
        : null,
    ]
    return _sections.filter((s) => s !== null) as Section[]
  }, [translate, attending, society, noReplyUsers, notAttending])

  return <SectionList sections={getNonEmptySections()} />
}
